/**
 * Default page_configuration rows for new groups_periods (team roles).
 * Applied via POST /api/page-configurations after a group-period is created.
 *
 * @see docs/group-role-templates.md
 */

import { resolveGroupIdsByNameTerms } from '../utils/groupsPage';

export const GROUP_ROLE_TEMPLATE_NONE = '';

/**
 * Resolved to role_group_id_excludes at template apply. Substring match on team name.
 * Owner teams omitted from excludes (see docs/organization-owner-teams.md).
 */
export const ADMIN_ROLE_HIDDEN_TEAM_NAME_TERMS = ['Super Admin', 'Super - Admin'];

/** Staff: hide elevated teams; includes Admin (also matches Super Admin via substring). */
export const STAFF_HIDDEN_TEAM_NAME_TERMS = ['Super Admin', 'Super - Admin', 'Admin'];

const BASIC_TEMPLATE_PERMISSIONS = [
  {
    page_name: 'dashboard',
    permission_type: 'page_configuration',
    configuration: { widgets: ['groups', 'profile'] }
  },
  {
    page_name: 'groups',
    permission_type: 'page_configuration',
    configuration: {
      view: 'team',
      can_view_group_members: true,
      view_group_members_in_own_group: true
    }
  },
  {
    page_name: 'profile',
    permission_type: 'page_configuration',
    configuration: {}
  }
];

/** View-only on User Profiles — flags omitted/false; page_configuration row still grants page access. */
const USER_PROFILES_READ_ONLY_CONFIG = {};

const USER_PROFILES_STUDENT_FORMS_CONFIG = {
  can_view_form: true,
  can_create_submission: true,
  can_view_submission: true,
  can_edit_draft: true,
  can_delete_submission: false,
  collaboration: {
    chat: { private: { read: true, write: true, edit: true } },
    flags: { read: true }
  },
  custom_forms_submission_scope: 'own_profile_only'
};

const USER_PROFILES_STAFF_FORMS_CONFIG = {
  can_view_form: true,
  can_create_submission: true,
  can_view_submission: true,
  can_edit_submission: true,
  can_delete_submission: true,
  can_view_question_answers: true,
  can_update_points: true,
  collaboration: {
    chat: {
      public: { read: true, write: true },
      internal: { read: true, write: true },
      private: { read: true, write: true, edit: true }
    },
    flags: { read: true, create: true, resolve: true, reopen: true, markAddressed: true }
  },
  custom_forms_submission_scope: 'assigned_students'
};

const USER_PROFILES_ADMIN_FORMS_CONFIG = {
  can_view_form: true,
  can_create_submission: true,
  can_view_submission: true,
  can_edit_draft: true,
  can_edit_submission: true,
  can_delete_submission: true,
  can_view_question_answers: true,
  can_update_points: true,
  collaboration: {
    chat: {
      public: { read: true, write: true },
      internal: { read: true, write: true },
      private: { read: true, write: true, edit: true }
    },
    flags: {
      read: true,
      create: true,
      resolve: true,
      reopen: true,
      markAddressed: true,
      createWorkSample: true
    },
    admin: { viewAllPrivateMessages: true }
  },
  custom_forms_submission_scope: 'platform'
};

/** @deprecated use scoped configs above */
const USER_PROFILES_CUSTOM_FORMS_CONFIG = {
  ...USER_PROFILES_STAFF_FORMS_CONFIG
};

const USER_PROFILES_FULL_EDIT_CONFIG = {
  can_edit_user_data: true,
  can_edit_sign_in: true,
  can_grant_platform_access: true,
  can_add_attachments: true,
  can_add_credentials: true,
  can_add_person_links: true,
  can_edit_attachments: true,
  can_edit_credentials: true,
  can_edit_person_links: true,
  can_add_associations: true,
  can_remove_associations: true,
  can_add_ugp_properties: true,
  can_remove_attachments: true,
  can_remove_credentials: true,
  can_edit_ugp_properties: true,
  can_remove_person_links: true,
  can_remove_ugp_properties: true,
  can_edit_student_information: true,
  ...USER_PROFILES_ADMIN_FORMS_CONFIG
};

const GROUPS_SUPER_ADMIN_CONFIG = {
  view: 'team',
  can_add_members: true,
  can_create_entities: true,
  add_members_to_own_group: true,
  can_create_organizations: false,
  can_open_member_profiles: true,
  can_view_group_members: true,
  can_view_member_details: true,
  can_view_member_access: true,
  create_entities_org_wide: false,
  can_add_members_same_level: true,
  create_entities_same_level: true,
  see_all_same_level_children: true,
  create_entities_in_own_group: true,
  open_member_profiles_to_own_group: false,
  can_open_member_profiles_same_level: true,
  view_group_members_in_own_group: true,
  can_view_group_members_same_level: true,
  view_member_details_in_own_group: true,
  can_view_member_details_same_level: true,
  view_member_access_in_own_group: true,
  can_view_member_access_same_level: true
};

/** @typedef {{ page_name: string, permission_type: 'page_configuration', configuration: object, resolveGroupIdExcludesFromNames?: string[] }} GroupRoleTemplatePermission */

/** @typedef {{ id: string, label: string, description: string, permissions: GroupRoleTemplatePermission[], resolveGroupIdExcludesFromNames?: string[] }} GroupRoleTemplate */

/** @type {GroupRoleTemplate[]} */
export const GROUP_ROLE_TEMPLATES = [
  {
    id: 'basic',
    label: 'Basic',
    description:
      'Dashboard: Groups + Profile widgets. Groups: team view, read-only. Profile: read-only.',
    permissions: [...BASIC_TEMPLATE_PERMISSIONS]
  },
  {
    id: 'staff',
    label: 'Staff',
    description:
      'Admin-style Groups access; hides Super Admin and Admin teams. Member profiles: view only; full Custom Forms access.',
    resolveGroupIdExcludesFromNames: [...STAFF_HIDDEN_TEAM_NAME_TERMS],
    permissions: [
      {
        page_name: 'dashboard',
        permission_type: 'page_configuration',
        configuration: { widgets: ['groups', 'profile'] }
      },
      {
        page_name: 'groups',
        permission_type: 'page_configuration',
        configuration: { ...GROUPS_SUPER_ADMIN_CONFIG }
      },
      {
        page_name: 'profile',
        permission_type: 'page_configuration',
        configuration: {}
      },
      {
        page_name: 'user-profiles',
        permission_type: 'page_configuration',
        configuration: {
          ...USER_PROFILES_READ_ONLY_CONFIG,
          ...USER_PROFILES_CUSTOM_FORMS_CONFIG
        }
      }
    ]
  },
  {
    id: 'super-admin',
    label: 'Super Admin',
    description:
      'Basic dashboard + own profile. Groups: team view with create/add members and same-level scope. User Profiles: full edit capabilities.',
    permissions: [
      {
        page_name: 'dashboard',
        permission_type: 'page_configuration',
        configuration: { widgets: ['groups', 'profile'] }
      },
      {
        page_name: 'groups',
        permission_type: 'page_configuration',
        configuration: { ...GROUPS_SUPER_ADMIN_CONFIG, can_view_derived_groups: true }
      },
      {
        page_name: 'profile',
        permission_type: 'page_configuration',
        configuration: {}
      },
      {
        page_name: 'user-profiles',
        permission_type: 'page_configuration',
        configuration: { ...USER_PROFILES_FULL_EDIT_CONFIG }
      }
    ]
  },
  {
    id: 'admin',
    label: 'Admin',
    description:
      'Like Super Admin, but hides Super Admin teams by group id (Admin teams stay visible).',
    resolveGroupIdExcludesFromNames: [...ADMIN_ROLE_HIDDEN_TEAM_NAME_TERMS],
    permissions: [
      {
        page_name: 'dashboard',
        permission_type: 'page_configuration',
        configuration: { widgets: ['groups', 'profile'] }
      },
      {
        page_name: 'groups',
        permission_type: 'page_configuration',
        configuration: { ...GROUPS_SUPER_ADMIN_CONFIG, can_view_derived_groups: true }
      },
      {
        page_name: 'profile',
        permission_type: 'page_configuration',
        configuration: {}
      },
      {
        page_name: 'user-profiles',
        permission_type: 'page_configuration',
        configuration: { ...USER_PROFILES_FULL_EDIT_CONFIG }
      }
    ]
  }
];

export function getGroupRoleTemplate(templateId) {
  if (!templateId || templateId === GROUP_ROLE_TEMPLATE_NONE) return null;
  return GROUP_ROLE_TEMPLATES.find((t) => t.id === templateId) || null;
}

/**
 * @param {GroupRoleTemplatePermission} perm
 * @param {{ allGroups?: object[], orgGroupId?: string|number, orgGpId?: string|number } | null} orgContext
 * @param {string[]} [templateResolveNames]
 */
function buildTemplatePermissionConfiguration(perm, orgContext, templateResolveNames = []) {
  const configuration = { ...perm.configuration };
  const nameTerms = [
    ...(templateResolveNames || []),
    ...(perm.resolveGroupIdExcludesFromNames || [])
  ];
  if (nameTerms.length > 0 && orgContext?.allGroups?.length) {
    const ids = resolveGroupIdsByNameTerms(
      orgContext.allGroups,
      orgContext.orgGroupId,
      orgContext.orgGpId,
      nameTerms
    );
    if (ids.length > 0) {
      configuration.role_group_id_excludes = ids;
    }
    delete configuration.role_group_name_excludes;
  }
  return configuration;
}

/**
 * Create or update page_configuration permissions for one groups_periods grantee.
 * @param {{ templateId: string, groupsPeriodsId: string|number, fetchWithAuth: (url: string, init?: RequestInit) => Promise<Response>, orgContext?: { allGroups?: object[], orgGroupId?: string|number, orgGpId?: string|number } }} params
 */
export async function applyGroupRoleTemplatePermissions({
  templateId,
  groupsPeriodsId,
  fetchWithAuth,
  orgContext = null
}) {
  const template = getGroupRoleTemplate(templateId);
  if (!template) return { ok: true, skipped: true };

  const granteeId = String(groupsPeriodsId);
  const failures = [];
  const templateResolveNames = template.resolveGroupIdExcludesFromNames || [];

  for (const perm of template.permissions) {
    const configuration = buildTemplatePermissionConfiguration(
      perm,
      orgContext,
      perm.page_name === 'groups' ? templateResolveNames : []
    );
    const res = await fetchWithAuth('/api/page-configurations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page_name: perm.page_name,
        grantee_type: 'groups_periods',
        grantees: [granteeId],
        permission_type: perm.permission_type,
        configuration
      })
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      failures.push({
        page: perm.page_name,
        message: data.message || `HTTP ${res.status}`
      });
    }
  }

  if (failures.length > 0) {
    return {
      ok: false,
      templateLabel: template.label,
      failures
    };
  }

  return { ok: true, templateLabel: template.label };
}
